import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Spinner, Text } from '@televet/kibble-ui';
import { Box } from '@televet/kibble-ui/chakra';
import { useAuth } from '@/providers/auth';
import { useEffect, useRef, useState } from 'react';
import { HOME_ROUTE } from '@/lib/constants';

interface TokenLoginSearch {
  token?: string;
  impersonate?: string;
}

export const Route = createFileRoute('/token-login')({
  component: TokenLogin,
  validateSearch: (search: Record<string, unknown>): TokenLoginSearch => {
    return {
      token: search.token as string | undefined,
      impersonate: search.impersonate as string | undefined,
    };
  },
});

function TokenLogin() {
  const { token } = Route.useSearch();
  const { loginWithCustomToken } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // `loginWithCustomToken` and `navigate` are not stable across auth-state settling, so
  // this effect can re-run after the first login already succeeded. Without the guard each
  // re-run queues another deferred navigate to /home, which lands ~500ms later and yanks the
  // user off whatever page they have since navigated to — a real bug for anyone who clicks a
  // nav link promptly after landing, and the cause of a flaky shell-navigation E2E test.
  const hasStartedLoginRef = useRef(false);

  useEffect(() => {
    if (hasStartedLoginRef.current) return;
    hasStartedLoginRef.current = true;

    const performLogin = async () => {
      if (!token) {
        setError('No token provided');
        return;
      }

      try {
        await loginWithCustomToken({ token });

        // Deferred so the auth state has propagated before _authenticated's beforeLoad
        // guard reads it; replace so /token-login (and the token in its query string)
        // does not sit in history behind /home.
        setTimeout(() => {
          navigate({ to: HOME_ROUTE, replace: true });
        }, 500);
      } catch (err) {
        console.error('Token login failed:', err);
        setError('Failed to login with token. Please try again or contact support.');
      }
    };

    performLogin();
  }, [token, loginWithCustomToken, navigate]);

  if (error) {
    return (
      <Box p={8} display="flex" justifyContent="center">
        <Alert status="error" maxW="md" hideCloseButton title="Login Failed" description={error} />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      h="100vh"
      gap={4}
    >
      <Spinner size="xl" />
      <Text>Logging in...</Text>
    </Box>
  );
}
